import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import classnames from 'classnames';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';

import { DescriptionMarkdownRenderer } from 'src/app/graph/documentationPage/schemaPage/descriptionMarkdownRenderer/DescriptionMarkdownRenderer';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { CoordinateKind } from 'src/lib/graphqlTypes/types';

import { FieldSummaryEmptyText } from '../FieldSummaryEmptyText';

const noDescriptionTextByCoordinateKind = new Map<CoordinateKind, string>([
  [CoordinateKind.ENUM_VALUE, 'No description available for this enum value.'],
  [
    CoordinateKind.INPUT_OBJECT_FIELD,
    'No description available for this input field.',
  ],
  [CoordinateKind.OBJECT_FIELD, 'No description available for this field.'],
]);

export const FieldPageDescription = ({
  description,
  coordinateKind,
}: {
  description?: string | null;
  coordinateKind: CoordinateKind;
}) => {
  const descriptionContainer = useRef<HTMLDivElement>(null);
  const fadeOut: CSSProperties = {
    WebkitMaskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
    maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
    lineHeight: '1.5rem',
  };

  const [showMore, setShowMore] = useState(false);
  const [contentOverflow, setContentOverflow] = useState(false);
  useEffect(() => {
    if (descriptionContainer?.current) {
      const desc = descriptionContainer?.current;
      const divHeight = desc.offsetHeight;
      // Get value in pixels and remove 'px' suffix from string
      const lineHeight = parseInt(
        getComputedStyle(desc).getPropertyValue('line-height').slice(0, -2),
        10,
      );
      const numLines = Math.ceil(divHeight / lineHeight);
      // Check if the div will take up more than three lines
      if (numLines > 3) {
        setContentOverflow(true);
        setShowMore(true);
      }
    }
  }, [descriptionContainer, setContentOverflow, setShowMore]);

  return (
    <>
      <div
        ref={descriptionContainer}
        className={classnames('break-words pb-2 text-base text-secondary', {
          'line-clamp-3': showMore,
          'line-clamp-none': !showMore,
        })}
        style={contentOverflow && showMore ? fadeOut : { lineHeight: '1.5rem' }}
      >
        {!description ? (
          <FieldSummaryEmptyText
            message={
              noDescriptionTextByCoordinateKind.get(coordinateKind) ??
              assertUnreachable(coordinateKind as never)
            }
          />
        ) : (
          <DescriptionMarkdownRenderer
            descriptionMarkdown={description}
            typeMap={{}}
          />
        )}
      </div>
      {contentOverflow && (
        <ClickableText
          as="div"
          className="text-base font-semibold text-link underline"
          onClick={() => setShowMore(!showMore)}
        >
          {showMore ? (
            <>
              Show More <IconChevronDown />
            </>
          ) : (
            <>
              Show Less <IconChevronUp />
            </>
          )}
        </ClickableText>
      )}
    </>
  );
};
